import type { CustomerDisplayPoster } from "@/services/setting";
import {
  DEFAULT_CUSTOMER_DISPLAY_POSTERS,
  normalizeCustomerDisplayPosters,
} from "@/utils/customerDisplayPosters";

export type CustomerDisplayMode = "idle" | "order" | "completed";

export interface CustomerDisplayLineItem {
  id: number | string;
  name: string;
  quantity: number;
  unit_price: number;
  base_total: number;
  discount_amount: number;
  discount_value?: number;
  discount_type?: "fixed" | "percentage";
  size_name?: string | null;
  sugar_name?: string | null;
  total: number;
}

export interface CustomerDisplayPromotion {
  code: string;
  name: string;
  description?: string | null;
  value: number;
  discount_type: "fixed" | "percentage";
  applied_amount: number;
  customer_name?: string | null;
  valid_from?: string | null;
  valid_till?: string | null;
  valid_day?: string | null;
  is_customer_specific?: boolean;
}

export interface CustomerDisplayOrderSnapshot {
  reference_no: string;
  customer_name: string;
  warehouse_name: string;
  payment_method: string;
  items_count: number;
  subtotal: number;
  discount: number;
  coupon_discount: number;
  tax: number;
  shipping: number;
  grand_total: number;
  amount_received: number;
  change_amount: number;
  items: CustomerDisplayLineItem[];
  promotion: CustomerDisplayPromotion | null;
}

export interface CustomerDisplayBillSnapshot {
  id: number;
  reference_no: string;
  customer_name: string;
  warehouse_name: string;
  biller_name: string;
  total: number;
  discount: number;
  coupon_discount: number;
  tax: number;
  shipping: number;
  grand_total: number;
  paid: number;
  due: number;
  status: string;
  payment_status: string;
  paid_by: string;
  date: string;
  cash_received: number;
  change_amount: number;
  items: CustomerDisplayLineItem[];
  promotion: CustomerDisplayPromotion | null;
}

export interface CustomerDisplayBranding {
  site_name: string;
  logo: string;
  company_phone: string;
  company_address: string;
  company_email: string;
  note_to_customer: string;
  currency_symbol: string;
  posters: CustomerDisplayPoster[];
}

export interface CustomerDisplayState {
  version: 1;
  mode: CustomerDisplayMode;
  updated_at: string;
  branding: CustomerDisplayBranding;
  order: CustomerDisplayOrderSnapshot | null;
  bill: CustomerDisplayBillSnapshot | null;
}

export const POS_CUSTOMER_DISPLAY_STORAGE_KEY = "pos_customer_display_state";
export const POS_CUSTOMER_DISPLAY_EVENT = "pos-customer-display-updated";
export const QR_PAYMENT_METHODS = ["aba", "khqr", "aclida"];

const DEFAULT_BRANDING: CustomerDisplayBranding = {
  site_name: "Cafe POS",
  logo: "",
  company_phone: "",
  company_address: "",
  company_email: "",
  note_to_customer: "Thanks for visiting us today.",
  currency_symbol: "$",
  posters: normalizeCustomerDisplayPosters(DEFAULT_CUSTOMER_DISPLAY_POSTERS),
};

const normalizeCustomerDisplayBranding = (
  input?: Partial<CustomerDisplayBranding> | null
): CustomerDisplayBranding => ({
  ...DEFAULT_BRANDING,
  ...(input || {}),
  posters: normalizeCustomerDisplayPosters(input?.posters),
});

export const createDefaultCustomerDisplayState = (): CustomerDisplayState => ({
  version: 1,
  mode: "idle",
  updated_at: new Date().toISOString(),
  branding: normalizeCustomerDisplayBranding(),
  order: null,
  bill: null,
});

export const isQrPaymentMethod = (value?: string | null): boolean =>
  QR_PAYMENT_METHODS.includes(String(value || "").trim().toLowerCase());

export const formatCustomerPaymentMethod = (value?: string | null): string =>
  String(value || "cash")
    .replace(/_/g, " ")
    .trim()
    .toUpperCase();

export const buildCustomerPaymentQrValue = (input: {
  siteName: string;
  referenceNo: string;
  total: number;
  paymentMethod?: string | null;
  customerName?: string | null;
}) => {
  const parts = [
    input.siteName || "Cafe POS",
    `Reference: ${input.referenceNo || "-"}`,
    `Amount: ${Number(input.total || 0).toFixed(2)}`,
    `Method: ${formatCustomerPaymentMethod(input.paymentMethod)}`,
  ];

  if (input.customerName) {
    parts.push(`Customer: ${input.customerName}`);
  }

  return parts.join("\n");
};

export const writeCustomerDisplayState = (state: CustomerDisplayState) => {
  if (typeof window === "undefined") return;

  const payload: CustomerDisplayState = {
    ...createDefaultCustomerDisplayState(),
    ...state,
    version: 1,
    updated_at: state.updated_at || new Date().toISOString(),
    branding: normalizeCustomerDisplayBranding(state.branding),
  };

  window.localStorage.setItem(
    POS_CUSTOMER_DISPLAY_STORAGE_KEY,
    JSON.stringify(payload)
  );
  window.dispatchEvent(
    new CustomEvent(POS_CUSTOMER_DISPLAY_EVENT, { detail: payload })
  );
};

export const readCustomerDisplayState = (): CustomerDisplayState => {
  if (typeof window === "undefined") {
    return createDefaultCustomerDisplayState();
  }

  try {
    const raw = window.localStorage.getItem(POS_CUSTOMER_DISPLAY_STORAGE_KEY);
    if (!raw) return createDefaultCustomerDisplayState();

    const parsed = JSON.parse(raw) as Partial<CustomerDisplayState>;

    return {
      ...createDefaultCustomerDisplayState(),
      ...parsed,
      branding: normalizeCustomerDisplayBranding(parsed.branding),
      order: parsed.order || null,
      bill: parsed.bill || null,
    };
  } catch {
    return createDefaultCustomerDisplayState();
  }
};

export const syncCustomerDisplayBranding = (
  branding: Partial<CustomerDisplayBranding>
) => {
  if (typeof window === "undefined") return;

  const current = readCustomerDisplayState();

  writeCustomerDisplayState({
    ...current,
    updated_at: new Date().toISOString(),
    branding: normalizeCustomerDisplayBranding({
      ...current.branding,
      ...branding,
    }),
  });
};
